import { useGetFaireAcademyPageState } from "@faire/deprecated-retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import { isLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import {
  useHeaderImagesFaireAcademy,
  IHeaderImage,
  IHeaderImageDimensions,
} from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/components/HeaderCard/utils";
import { SignUpButton } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/components/SignUpButton";
import { OWF_BACKGROUND } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/imagesData";
import { OWF_SECTION_NAME } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/types";
import { Core } from "@faire/design-tokens";
import { trackOpenWithFaireFaireAcademySectionView } from "@faire/web-api--source/events/openWithFaire/view/faireAcademySection";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { Column } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { SlideIn } from "@faire/web/ui/SlideIn/SlideIn";
import React from "react";
import styled, { css } from "styled-components";

export const HeaderCardFaireAcademy: React.FC = () => {
  const { pageState } = useGetFaireAcademyPageState();
  const ref = useDoActionWhenVisible(() =>
    trackOpenWithFaireFaireAcademySectionView(
      pageState,
      OWF_SECTION_NAME.HEADER_CARD
    )
  );

  return (
    <HeaderWrapper ref={ref} data-test-id="open-with-faire-academy-title">
      <HeaderImages />
      <SlideIn offset="50px">
        <HeaderContent>
          <HeaderTitle />
          <HeaderBody />
        </HeaderContent>
      </SlideIn>
    </HeaderWrapper>
  );
};

const HeaderTitle: React.FC = () => {
  return (
    <>
      <Typography
        as="h1"
        color={Core.surface.inverse}
        variant={{
          mobileAndAbove: "paragraphSansMedium",
          desktopAndAbove: "subheadingSansMedium",
        }}
      >
        <StrictLocalMsg
          id="+vVy5g"
          defaultMessage="Open with Faire"
          description={{
            text: "Name of a program we offer for helping opening retailers to start their business",
          }}
        />
      </Typography>
      <Spacer
        height={{
          mobileAndAbove: "2x",
          desktopAndAbove: "3x",
        }}
      />
      <Typography
        align="center"
        color={Core.surface.inverse}
        variant={{
          mobileAndAbove: "displayMSerifRegular",
          tabletAndAbove: "displayLSerifRegular",
          desktopAndAbove: "displayXLSerifRegular",
        }}
      >
        <StrictLocalMsg
          id="gDE6J+"
          defaultMessage="Dream store,"
          description={{
            text: "Header card title for Open with Faire, a program we offer for helping opening retailers to start their business",
            img: "https://cdn.faire.com/fastly/cfc5b0d3ed9509ab4818e9b1e2f6c222949b6ab429467fabad21d6a9346c2d70.png",
          }}
        />
        <br />
        <StrictLocalMsg
          id="uCcbYL"
          defaultMessage="here we come"
          description={{
            text: "Header card title for Open with Faire, a program we offer for helping opening retailers to start their business",
            img: "https://cdn.faire.com/fastly/cfc5b0d3ed9509ab4818e9b1e2f6c222949b6ab429467fabad21d6a9346c2d70.png",
          }}
        />
      </Typography>
    </>
  );
};

const HeaderBody: React.FC = () => {
  if (isLoggedInRetailer()) {
    return (
      <HeaderBodyColumn>
        <Spacer height="3x" />
        <Typography
          color={Core.surface.inverse}
          variant={{
            mobileAndAbove: "paragraphSansRegular",
            desktopAndAbove: "subheadingSansRegular",
          }}
        >
          <StrictLocalMsg
            id="oo8o5n"
            defaultMessage="Starting a retail business can be hard. That’s why we created Open with Faire—to help new retailers like you navigate challenges along the road to opening their dream store."
            description={{
              text: "Motivation statement for Open with Faire, a program we offer for helping opening retailers to start their business",
              img: "https://cdn.faire.com/fastly/2b0e55f1ff17a480eeb9764ad269be8e088275fc5b974bb43ef94dc04ea58d3a.png",
            }}
          />
        </Typography>
      </HeaderBodyColumn>
    );
  }

  return (
    <>
      <Spacer
        height={{
          mobileAndAbove: "5x",
          desktopAndAbove: "6x",
        }}
      />
      <SignUpButton source={OWF_SECTION_NAME.HEADER_CARD} />
    </>
  );
};

const HeaderImages: React.FC = () => {
  const { isMobile, isTablet, isDesktop } = useViewport();
  const headerImagesFaireAcademy = useHeaderImagesFaireAcademy();

  const getImageDimensions = (dimensions: {
    [viewport: string]: IHeaderImageDimensions;
  }) => {
    if (isMobile) {
      return dimensions.mobile;
    } else if (isTablet) {
      return dimensions.tablet;
    } else if (isDesktop) {
      return dimensions.desktop;
    } else {
      return dimensions.xlarge;
    }
  };

  return (
    <ImagesWrapper>
      {headerImagesFaireAcademy.map((image: IHeaderImage) => {
        const imageDimensions = getImageDimensions(image.dimensions);

        return (
          <StyledImage
            key={image.src}
            src={image.src}
            $zIndex={image.zIndex}
            style={{
              height: imageDimensions?.height,
            }}
            $top={imageDimensions?.top}
            $left={imageDimensions?.left}
            $right={imageDimensions?.right}
            alt=""
          />
        );
      })}
    </ImagesWrapper>
  );
};

const HeaderWrapper = styled(Column).attrs({
  align: "center",
  justify: {
    mobileAndAbove: "end",
    tabletAndAbove: "center",
  },
})`
  height: 613px;
  width: 100%;
  background-image: url(${OWF_BACKGROUND});

  ${responsiveMediaQueries.tablet} {
    height: 525px;
  }
  ${responsiveMediaQueries.mobile} {
    height: 652px;
  }
`;

const HeaderContent = styled(Column).attrs({
  align: "center",
  justify: "center",
})`
  ${responsiveMediaQueries.mobile} {
    width: 100%;
    margin-bottom: ${getSpacing("8x")};
  }
`;

const HeaderBodyColumn = styled(Column)`
  text-align: center;
  max-width: 417px;

  ${responsiveMediaQueries.tablet} {
    width: 296px;
  }
  ${responsiveMediaQueries.mobile} {
    max-width: 257px;
  }
`;

const StyledImage = styled.img<{
  $zIndex?: number;
  $top?: string;
  $left?: string;
  $right?: string;
}>`
  position: absolute;
  display: inline;

  ${(props) =>
    props.$zIndex
      ? css`
          z-index: ${props.$zIndex};
        `
      : css`
          z-index: 1;
        `};
  ${(props) => css`
    top: ${props?.$top};
    left: ${props?.$left};
    right: ${props?.$right};
  `}
`;

const ImagesWrapper = styled.div`
  ${responsiveMediaQueries.xLargeAndAbove} {
    position: relative;
    width: 1440px;
    height: 0;
  }
  ${responsiveMediaQueries.mobile} {
    position: relative;
    width: 320px;
    height: 0;
  }
`;
